import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Localization from '../../../Localization';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';

const AIAssessmentButton = ({ submissionList, root, localeId, submissionDate, schoolId, onAssessmentComplete, onAssessmentStart, buttonType = 'start', progressPercentage = 0, progressDetails, isAssessmentInProgress, answersDetail, onNoAnswers }) => {
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [totalAnswers, setTotalAnswers] = useState(0);
  const [remainingAnswers, setRemainingAnswers] = useState(0);

  // Update result message when progress details change
  useEffect(() => {
    if (progressDetails && progressDetails.is_complete && !isAssessmentInProgress) {
      const successfulCount = progressDetails.completed_successfully || 0;
      const failedCount = progressDetails.failed || 0;
      const totalCount = progressDetails.total_answers || 0;
      
      setResultMessage(formatResultMessage(
        successfulCount,
        totalCount,
        failedCount
      ));
      setIsProcessing(false);
    }
  }, [progressDetails, isAssessmentInProgress]);
  
  // Handle changes in assessment progress status
  useEffect(() => {
    // If assessment was in progress but is now complete
    if (!isAssessmentInProgress && progressDetails && progressDetails.is_complete) {
      setIsProcessing(false);
    }
  }, [isAssessmentInProgress, progressDetails]);

  const handleClick = () => {
    setOpen(true);
    
    // If we already have results, just show them instead of fetching totals again
    if (resultMessage) {
      return;
    }
    
    setResultMessage('');
    // Get total answers when dialog opens and check if we should proceed
    const params = {
      locale_id: localeId,
      date: submissionDate,
      school_id: schoolId
    };

    root.refs.API.request(
      "get_total_answers_number_by_epack_and_date",
      params,
      (res) => {
        const total = res?.body?.result?.total || 0;
        setTotalAnswers(total);
        
        // Check if there are no assessable questions
        if (total === 0 && buttonType === 'start') {
          setOpen(false); // Close the dialog
          if (onNoAnswers) {
            onNoAnswers();
          }
          return;
        }
        
        // After getting total, get assessed answers to calculate remaining if it's a resume
        if (buttonType === 'resume') {
          root.refs.API.request(
            "get_total_assessed_answers_number_by_epack_and_date",
            params,
            (assessedRes) => {
              const assessed = assessedRes?.body?.result?.total || 0;
              setRemainingAnswers(total - assessed);
            },
            (err) => console.error('Failed to get assessed answers:', err)
          );
        }
      },
      (err) => console.error('Failed to get total answers:', err)
    );
  };

  const handleClose = () => {
    if (!isProcessing) {
      setOpen(false);
      setResultMessage('');
      setTotalAnswers(0);
      setRemainingAnswers(0);
    }
  };

  const formatEstimatedTime = (totalAnswers) => {
    const minutes = Math.floor(totalAnswers * 0.1);
    if (minutes < 1) {
      const seconds = Math.round(totalAnswers * 0.1 * 60);
      return `${seconds}秒`;
    }
    return `${minutes}分鐘`;
  };

  const formatResultMessage = (total_processed, total_answers, total_failed) => {
    const template = buttonType === 'resume' 
      ? (Localization.strings.RESUME_AI_ASSESSMENT_RESULT || 'Resume Assessment Complete: {0} out of {1} answers processed ({2} failed)')
      : (Localization.strings.AI_ASSESSMENT_RESULT || 'Assessment Complete: {0} out of {1} answers processed ({2} failed)');
      
    return template
      .replace('{0}', total_processed)
      .replace('{1}', total_answers)
      .replace('{2}', total_failed);
  };

  const handleBatchAssessmentSuccess = (res) => {
    const result = res?.body?.result?.result;
    if (!result) {
      handleBatchAssessmentError(new Error('Malformed assessment response'));
      return;
    }
    setResultMessage(formatResultMessage(
      result.total_processed,
      result.total_answers,
      result.total_failed
    ));
    setIsProcessing(false);

    // Call parent's refresh function
    if (onAssessmentComplete) {
      onAssessmentComplete();
    }
  };

  // Handle assessment completion from parent component
  const handleAssessmentComplete = (results) => {
    if (results) {
      // Use the same field names the batch-progress result carries (total_processed /
      // total_answers / total_failed); successCount/totalCount/failureCount don't exist.
      setResultMessage(formatResultMessage(
        results.total_processed,
        results.total_answers,
        results.total_failed
      ));
      setIsProcessing(false);
    }
  };

  const handleBatchAssessmentError = (err) => {
    console.error('Batch assessment failed:', err);
    setResultMessage(Localization.strings.AI_ASSESSMENT_ERROR);
    setIsProcessing(false);
  };

  const handleConfirm = () => {
    setIsProcessing(true);
    setResultMessage('');

    // Call onAssessmentStart callback when assessment begins
    if (onAssessmentStart) {
      console.log('Calling onAssessmentStart from AIAssessmentButton');
      onAssessmentStart();
    }

    // Keep dialog open to show progress
    // Note: Dialog will show progress while assessment is in progress
  };

  // Determine button label based on type
  const getButtonLabel = () => {
    if (buttonType === 'resume') {
      return Localization.strings.RESUME_AI_ASSESSMENT;
    } else {
      return Localization.strings.RE_EXECUTE_AI_ASSESSMENT || Localization.strings.START_NEW_AI_ASSESSMENT;
    }
  };

  // Get the appropriate warning message based on button type
  const getWarningMessage = () => {
    if (buttonType === 'resume') {
      return Localization.strings.RESUME_AI_ASSESSMENT_WARNING;
    } else {
      return Localization.strings.RE_EXECUTE_AI_ASSESSMENT_WARNING || Localization.strings.START_NEW_AI_ASSESSMENT_WARNING;
    }
  };

  // Get the time estimate message based on button type
  const getTimeEstimate = () => {
    if (buttonType === 'resume' && remainingAnswers > 0) {
      return formatEstimatedTime(remainingAnswers);
    } else if (totalAnswers > 0) {
      return formatEstimatedTime(totalAnswers);
    }
    return '';
  };

  // Format the progress information for display in dialog
  const formatProgressInfo = () => {
    if (!progressDetails) return null;
    
    const {
      total_answers,
      completed_successfully,
      failed,
      pending,
      percentage_complete
    } = progressDetails;
    
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {Localization.strings.ASSESSMENT_PROGRESS || 'Assessment Progress'}:
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={percentage_complete || 0} 
          sx={{ mb: 1, height: 10, borderRadius: 5 }}
        />
        <Typography variant="body2" align="center" sx={{ mb: 2 }}>
          {percentage_complete || 0}%
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <Typography variant="body2">
            {Localization.strings.TOTAL_ANSWERS || 'Total Answers'}: 
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {total_answers || 0}
          </Typography>
          
          <Typography variant="body2">
            {Localization.strings.COMPLETED_SUCCESSFULLY || 'Completed'}: 
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {completed_successfully || 0}
          </Typography>
          
          <Typography variant="body2">
            {Localization.strings.FAILED || 'Failed'}: 
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="error.main">
            {failed || 0}
          </Typography>
          
          <Typography variant="body2">
            {Localization.strings.PENDING || 'Pending'}: 
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="info.main">
            {pending || 0}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {(() => {
        // Check subscription info for AI_AUTO_ASSESSMENT permission
        const canUseAIAssessment = root.getPrivilege('AI_AUTO_ASSESSMENT').value === '1';
        const isDisabled = !canUseAIAssessment || isAssessmentInProgress;
        const tooltipTitle = !canUseAIAssessment ? Localization.strings.UPGRADE_TO_PREMIUM : null; // Only show premium tooltip

        const buttonContent = (
          <Button
            variant="contained"
            onClick={handleClick}
            disabled={isDisabled}
            sx={{
              backgroundColor: buttonType === 'resume' ? '#ff9800' : '#ad1179',
              '&:hover': {
                backgroundColor: buttonType === 'resume' ? '#fb8c00' : 'rgba(173, 17, 121, 0.8)'
              },
              '&.Mui-disabled': {
                backgroundColor: buttonType === 'resume' ? 'rgba(255, 152, 0, 0.4)' : 'rgba(173, 17, 121, 0.4)'
              },
              minWidth: '180px'
            }}
          >
            {getButtonLabel()}
          </Button>
        );

        if (tooltipTitle) {
          return (
            <Tooltip title={tooltipTitle}>
              <span> {/* Span needed for disabled Button */}
                {buttonContent}
              </span>
            </Tooltip>
          );
        } else {
          return buttonContent;
        }
      })()}

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="ai-assessment-dialog-title"
        aria-describedby="ai-assessment-dialog-description"
        PaperProps={{
          sx: {
            width: '500px',
            minHeight: '300px',
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle 
          id="ai-assessment-dialog-title"
          style={{
            backgroundColor: '#ad1179', 
            color: '#FFFFFF',
            marginBottom: '15px'
          }}
        >
          {resultMessage ? Localization.strings.ASSESSMENT_COMPLETE : (Localization.strings.RE_EXECUTE_AI_ASSESSMENT || Localization.strings.START_NEW_AI_ASSESSMENT)}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '180px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {!isProcessing && !resultMessage ? (
            <>
              <DialogContentText id="ai-assessment-dialog-description">
                {getWarningMessage()}
              </DialogContentText>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {getTimeEstimate() ? `${Localization.strings.TIME_ESTIMATED} ${getTimeEstimate()}` : ''}
              </Typography>
            </>
          ) : resultMessage ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h6" align="center" color="primary" gutterBottom>
                {Localization.strings.ASSESSMENT_COMPLETE}
              </Typography>
              <DialogContentText id="ai-assessment-dialog-description" align="center">
                {resultMessage}
              </DialogContentText>
              <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                {Localization.strings.SAFE_TO_CLOSE}
              </Typography>
            </Box>
          ) : isProcessing && isAssessmentInProgress ? (
            <>
              <DialogContentText id="ai-assessment-dialog-description">
                {Localization.strings.ASSESSMENT_IN_PROGRESS}
              </DialogContentText>
              <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 'bold' }}>
                {Localization.strings.DO_NOT_CLOSE}
              </Typography>
              {/* Display progress information when assessment is in progress */}
              {formatProgressInfo()}
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={isProcessing && !resultMessage}
          >
            {resultMessage ? Localization.strings.CLOSE : Localization.strings.CANCEL}
          </Button>
          {!isProcessing && !resultMessage && (
            <Button 
              onClick={handleConfirm} 
              variant="contained" 
              color="primary"
              disabled={isProcessing}
            >
              {Localization.strings.CONFIRM_AI_ASSESSMENT}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIAssessmentButton;